import type { FraudVerdictResponse } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchFraudVerdictApi } from "../../../api/client";
import { isHttpError } from "../../../lib/query/errors";

/**
 * Shared queryOptions for a deal's Informed fraud verdict (key + queryFn).
 * Backed by the generated `FraudVerdictApi.getFraudVerdict`, which forwards to
 * chassis's Informed Defender lookup. A 404 means chassis hasn't yet computed
 * a verdict for this approval — treated as "not flagged" here so callers
 * never need to special-case it. `staleTime: 0` is deliberate (not the app-wide
 * 30s convention): SVRS-57313's entry/exit comparison requires every read —
 * both the baseline read and the exit-time re-check — to reflect the live
 * verdict, never a cached one.
 * @param {string} approvalId - The numeric approval / vehicle-loan-application id.
 * @param {boolean} [enabled] - Additional gate beyond `approvalId` being present — see
 * {@link useFraudVerdict}. Defaults to `true` for the manual `queryClient.fetchQuery`
 * exit-time re-check, which ignores `enabled` entirely and always fetches on demand.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function fraudVerdictQuery(approvalId: string, enabled = true) {
  return queryOptions({
    queryKey: ["fraud-verdict", approvalId] as const,
    queryFn: async ({ signal }): Promise<FraudVerdictResponse> => {
      try {
        const response = await workbenchFraudVerdictApi.getFraudVerdict(approvalId, { signal });
        return response.data;
      } catch (error) {
        if (isHttpError(error, 404)) {
          return { fraudDetected: false };
        }
        throw error;
      }
    },
    staleTime: 0,
    enabled: !!approvalId && enabled,
  });
}

/**
 * Reads the Informed fraud verdict for a deal. Disabled (never fires) until the
 * approval id is present and `enabled` is true — callers should hold off until
 * role/VLA-lock/editability checks have resolved and the Save button is actually
 * visible, since a read-only or still-loading analyst has nothing to compare an
 * exit-time re-check against.
 * @param {string} approvalId - The numeric approval / vehicle-loan-application id.
 * @param {boolean} [enabled] - Whether the query should run at all. Defaults to `true`.
 * @returns {ReturnType<typeof useQuery<FraudVerdictResponse>>} The TanStack Query result.
 */
export function useFraudVerdict(approvalId: string, enabled = true) {
  return useQuery(fraudVerdictQuery(approvalId, enabled));
}
